import { Dispatch } from '@reduxjs/toolkit';

import { ICartEntry } from '@rbi-ctg/menu';
import {
  IHydrateCartInput,
  IMenuSelectionInput,
  Platform,
  ServiceMode,
  useHydrateCartMutation,
} from 'generated/graphql-gateway';
import { actions } from 'state/global-state';
import { IMinimalCartEntry } from 'state/global-state/models/ordering/cart/cart.types';
import { createCartEntriesFromQuantity } from 'utils/cart';
import { isWeb } from 'utils/environment';
import {
  buildMinimalCartEntriesLookup,
  findMatchingCartEntry,
  mapHydratedCart,
  mapServiceModeToCartServiceMode,
} from 'utils/minimal-cart';

import { IUseCart } from './use-cart-types';

// Had to extract this to a pure function to avoid a circular dep between use-cart and use-minimal-cart
export async function hydrateEntries(
  cartEntries: IMinimalCartEntry[],
  storeId: string,
  serviceMode: ServiceMode,
  hydrateCart: ReturnType<typeof useHydrateCartMutation>[0],
  addItemToCart: IUseCart['addItemToCart'],
  updateCartEntry: IUseCart['updateCartEntry'],
  dispatch: Dispatch,
  handleRewards?: (
    rewardBenefitId: string,
    cartEntry: ICartEntry,
    remainingPoints: { value: number }
  ) => ICartEntry,
  stagedCartPoints: number = 0
): Promise<boolean> {
  const channel = isWeb ? Platform.WEB : Platform.APP;
  const menuSelections: IMenuSelectionInput[] = cartEntries.map(entry => ({
    id: entry.id,
    quantity: entry.quantity,
    menuSelections: entry.menuSelections,
  }));
  const input: IHydrateCartInput = {
    cart: { menuSelections },
    serviceMode: mapServiceModeToCartServiceMode(serviceMode),
    storeId,
  };

  try {
    const hydrationResult = await hydrateCart({
      variables: { channel, input },
    });

    if (hydrationResult.errors) {
      throw hydrationResult.errors;
    }

    const hydratedCartEntries = mapHydratedCart(
      hydrationResult?.data?.hydrateCart.cartEntries ?? [],
      buildMinimalCartEntriesLookup(cartEntries)
    );

    // Initialize remainingPoints outside the loop
    const remainingPoints = { value: stagedCartPoints };

    hydratedCartEntries.forEach(cartEntry => {
      const matchingMinimalCartEntry = findMatchingCartEntry(cartEntries, cartEntry);
      if (matchingMinimalCartEntry?.isEdit) {
        updateCartEntry(cartEntry, cartEntry.cartId, true, true);
      } else if (handleRewards && matchingMinimalCartEntry?.rewardBenefitId) {
        const rewardBenefitId = matchingMinimalCartEntry.rewardBenefitId;
        // For rewards, we need to create a set of cartEntries based on the quantity.
        // This will ensure we can run one cartEntry at a time to avoid having race condition issues.
        const cartEntriesFromQuantity = createCartEntriesFromQuantity(cartEntry);

        cartEntriesFromQuantity.forEach(cartEntry => {
          // This will create the applied reward for the cartEntry.
          handleRewards(rewardBenefitId, cartEntry, remainingPoints);
          addItemToCart(cartEntry, undefined, false, true);
        });
      } else {
        addItemToCart(cartEntry, undefined, false, true);
      }
    });
    dispatch(actions.ordering.resetMinimalCart());
    return true;
  } catch (error) {
    dispatch(actions.ordering.resetMinimalCart());
    return false;
  }
}
